import { zodResolver } from '@hookform/resolvers/zod';
import {
  BUSINESS_TYPES, CONTACT_ROLE_LABELS, CONTACT_ROLES, LEAD_SOURCES, MATCH_REASON_LABELS, PRIORITIES, PRIORITY_LABELS, SOURCE_LABELS,
  organizationCreateSchema,
} from '@takumi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useInvalidateCrm, useUserOptions } from '@/lib/queries';
import type { DuplicateResult } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { FollowupScheduler, type ScheduleValue } from '../crm/FollowupScheduler';
import { followupPresetDate } from '@takumi/shared';

type FormIn = z.input<typeof organizationCreateSchema>;
type FormOut = z.output<typeof organizationCreateSchema>;

function useDebounced<T>(value: T, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function NewLeadButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" className="h-9">
        <Plus /> ליד חדש
      </Button>
      <NewOrganizationDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export function NewOrganizationDialog({ open, onOpenChange, asCustomer }: { open: boolean; onOpenChange: (o: boolean) => void; asCustomer?: boolean }) {
  const navigate = useNavigate();
  const invalidate = useInvalidateCrm();
  const users = useUserOptions();
  const { me } = useAuth();
  const [schedule, setSchedule] = useState<ScheduleValue>({ preset: 'tomorrow', dueAt: followupPresetDate('tomorrow') });

  const form = useForm<FormIn, unknown, FormOut>({
    resolver: zodResolver(organizationCreateSchema),
    defaultValues: {
      name: '',
      businessType: '',
      city: '',
      source: 'phone',
      priority: 'normal',
      ownerId: '',
      lifecycleStage: asCustomer ? 'customer' : 'lead',
      contact: { fullName: '', phone: '', email: '', role: 'owner' },
      note: '',
    },
  });
  const { register, handleSubmit, formState, reset, control, setError } = form;
  const errors = formState.errors;

  useEffect(() => {
    if (open) {
      reset({
        name: '', businessType: '', city: '', source: 'phone', priority: 'normal', ownerId: me?.user.id ?? '',
        lifecycleStage: asCustomer ? 'customer' : 'lead', contact: { fullName: '', phone: '', email: '', role: 'owner' }, note: '',
      });
      setSchedule({ preset: 'tomorrow', dueAt: followupPresetDate('tomorrow') });
    }
  }, [open, reset, asCustomer, me]);

  // Live duplicate check while typing (phone / email / business name).
  const watched = useWatch({ control, name: ['name', 'contact.phone', 'contact.email'] });
  const probe = useDebounced(watched);
  const dup = useQuery({
    queryKey: ['dupCheck', probe],
    queryFn: () => api.get<DuplicateResult>('/organizations/duplicate-check', { name: probe[0], phone: probe[1] ?? '', email: probe[2] ?? '' }),
    enabled: open && ((probe[0]?.length ?? 0) >= 2 || (probe[1]?.length ?? 0) >= 9 || (probe[2]?.includes('@') ?? false)),
  });

  const create = useMutation({
    mutationFn: (body: FormOut) => api.post<{ id: string; name: string }>('/organizations', body),
    onSuccess: (org) => {
      invalidate();
      toast.success(`${asCustomer ? 'הלקוח' : 'הליד'} "${org.name}" נוצר`);
      onOpenChange(false);
      navigate(`/org/${org.id}`);
    },
    onError: (e) => {
      if (e instanceof ApiError) e.issues.forEach((i) => setError(i.path as never, { message: i.message }));
      toast.error(errorMessage(e));
    },
  });

  const onSubmit = handleSubmit((values) => {
    const contact = values.contact?.fullName ? values.contact : null;
    create.mutate({ ...values, contact, firstFollowupAt: asCustomer ? null : schedule.dueAt });
  });

  const candidates = dup.data?.candidates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={asCustomer ? 'לקוח חדש' : 'ליד חדש'} wide>
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          {candidates.length > 0 && (
            <div role="alert" className={dup.data?.kind === 'strong' ? 'rounded-card border border-overdue/30 bg-overdue-bg p-3' : 'rounded-card border border-today/30 bg-today-bg p-3'}>
              <p className="flex items-center gap-2 text-sm font-bold text-ink">
                <AlertTriangle className="size-4 text-overdue" />
                {dup.data?.kind === 'strong' ? 'נראה שהעסק כבר קיים במערכת' : 'חשד לכפילות'}
              </p>
              <ul className="mt-2 flex flex-col gap-1 text-sm">
                {candidates.slice(0, 3).map((c) => (
                  <li key={c.organizationId} className="flex flex-wrap items-center gap-2">
                    <Link to={`/org/${c.organizationId}`} onClick={() => onOpenChange(false)} className="font-semibold text-matcha-800 underline underline-offset-2">
                      {c.organizationName}
                    </Link>
                    <span className="text-muted">{c.city}</span>
                    <span className="text-xs text-ink-soft">({c.reasons.map((r) => MATCH_REASON_LABELS[r]).join(', ')})</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted">אם זה אותו עסק — עדיף לפתוח את הרשומה הקיימת ולרשום שם את הפנייה.</p>
            </div>
          )}

          <section className="grid gap-4 sm:grid-cols-2">
            <Field label="שם העסק" required error={errors.name?.message} className="sm:col-span-2">
              {(id) => <Input id={id} autoFocus aria-invalid={!!errors.name} {...register('name')} placeholder="לדוגמה: קפה גן סיפור" />}
            </Field>
            <Field label="סוג העסק">
              {(id) => (
                <Select id={id} {...register('businessType')}>
                  <option value="">בחרו…</option>
                  {BUSINESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                </Select>
              )}
            </Field>
            <Field label="עיר">{(id) => <Input id={id} {...register('city')} />}</Field>
          </section>

          <section className="rounded-card border border-line bg-sunken/40 p-4">
            <h3 className="mb-3 text-sm font-bold text-ink-soft">איש קשר</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="שם מלא" error={errors.contact?.fullName?.message}>
                {(id) => <Input id={id} {...register('contact.fullName')} />}
              </Field>
              <Field label="תפקיד">
                {(id) => (
                  <Select id={id} {...register('contact.role')}>
                    {CONTACT_ROLES.map((r) => <option key={r} value={r}>{CONTACT_ROLE_LABELS[r]}</option>)}
                  </Select>
                )}
              </Field>
              <Field label="טלפון" error={errors.contact?.phone?.message}>
                {(id) => <Input id={id} type="tel" dir="ltr" className="text-start" placeholder="052-000-0000" aria-invalid={!!errors.contact?.phone} {...register('contact.phone')} />}
              </Field>
              <Field label="אימייל" error={errors.contact?.email?.message}>
                {(id) => <Input id={id} type="email" dir="ltr" className="text-start" aria-invalid={!!errors.contact?.email} {...register('contact.email')} />}
              </Field>
            </div>
          </section>

          <section className="grid gap-4 sm:grid-cols-3">
            <Field label="מקור">
              {(id) => (
                <Select id={id} {...register('source')}>
                  {LEAD_SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
                </Select>
              )}
            </Field>
            <Field label="עדיפות">
              {(id) => (
                <Select id={id} {...register('priority')}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                </Select>
              )}
            </Field>
            <Field label="אחראי">
              {(id) => (
                <Select id={id} {...register('ownerId')}>
                  {users.data?.map((u) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
                </Select>
              )}
            </Field>
          </section>

          <Field label="הערה ראשונה">{(id) => <Textarea id={id} rows={2} {...register('note')} placeholder="מה ביקשו? מאיפה הגיעו?" />}</Field>

          {!asCustomer && (
            <div className="rounded-card border border-matcha-200 bg-matcha-50/60 p-4">
              <div className="mb-3 flex items-center gap-2 font-semibold text-matcha-900">
                <CalendarClock className="size-4" /> פולואפ ראשון
              </div>
              <FollowupScheduler value={schedule} onChange={setSchedule} />
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={create.isPending}>{asCustomer ? 'יצירת לקוח' : 'יצירת ליד'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
